import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Video, Copy, ExternalLink, X, Users, CalendarX2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import NyttMoteModal from "./NyttMoteModal";

const MND = ["JAN", "FEB", "MAR", "APR", "MAI", "JUN", "JUL", "AUG", "SEP", "OKT", "NOV", "DES"];

const lokalNaa = () => {
  const d = new Date();
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:00`;
};

const MoteRad = ({ mote, onAvlys, passert }) => {
  const [aar, mnd, dag] = mote.date.split("-");
  const slutt = (mote.end || "").slice(11, 16);
  const dus = passert || mote.cancelled;
  return (
    <div className={`group flex items-center gap-4 py-3.5 border-t border-[#E7E4DB] first:border-t-0 ${dus ? "opacity-55" : ""}`} data-testid={`mote-rad-${mote.id}`}>
      <div className="w-12 text-center shrink-0">
        <p className="bud-mono text-[19px] font-bold leading-none text-neutral-900 tabular-nums">{dag}</p>
        <p className="bud-mono text-[9px] tracking-[0.2em] text-neutral-400 mt-1">{MND[Number(mnd) - 1]} {aar.slice(2)}</p>
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-[14px] font-medium text-neutral-900 truncate ${mote.cancelled ? "line-through text-neutral-400" : ""}`}>{mote.subject}</p>
        <p className="text-[11.5px] text-neutral-500 mt-0.5">
          <span className="bud-mono tabular-nums">{mote.startTime}–{slutt}</span>
          {mote.attendees.length > 0 && (
            <span className="inline-flex items-center gap-1 ml-2.5" title={mote.attendees.join(", ")}>
              <Users size={10.5} /> {mote.attendees.length}
            </span>
          )}
          {mote.cancelled && <span className="ml-2.5 text-red-500 font-medium">Avlyst</span>}
        </p>
      </div>
      {mote.teams && mote.joinUrl && !mote.cancelled && (
        <div className="flex items-center gap-1 shrink-0">
          <a
            href={mote.joinUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-indigo-700 bg-indigo-50 rounded-full px-2.5 py-1 hover:bg-indigo-100 transition-colors"
            data-testid={`mote-teams-${mote.id}`}
          >
            <Video size={11.5} /> Teams
          </a>
          <button
            type="button"
            onClick={() => { navigator.clipboard.writeText(mote.joinUrl); toast.success("Teams-lenke kopiert"); }}
            className="p-1.5 text-neutral-300 hover:text-neutral-700 transition-colors"
            title="Kopier Teams-lenke"
            data-testid={`mote-kopier-${mote.id}`}
          >
            <Copy size={13} />
          </button>
        </div>
      )}
      {mote.teams && !mote.joinUrl && !mote.cancelled && (
        <span className="shrink-0 text-[10.5px] font-medium text-amber-700 bg-amber-50 rounded-full px-2 py-0.5" title="Teams er ikke aktivert for arrangørens postboks i Microsoft 365" data-testid={`mote-teams-mangler-${mote.id}`}>
          Uten Teams-lenke
        </span>
      )}
      {mote.webLink && (
        <a href={mote.webLink} target="_blank" rel="noreferrer" className="hidden sm:block p-1.5 text-neutral-300 hover:text-neutral-700 transition-colors opacity-0 group-hover:opacity-100" title="Åpne i Outlook">
          <ExternalLink size={13} />
        </a>
      )}
      {!mote.cancelled && !passert && (
        <button
          type="button"
          onClick={() => onAvlys(mote)}
          className="p-1.5 text-neutral-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
          title="Avlys møtet — deltakerne varsles"
          data-testid={`mote-avlys-${mote.id}`}
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
};

export default function MoterAdmin() {
  const [data, setData] = useState(null);
  const [members, setMembers] = useState([]);
  const [nyttMote, setNyttMote] = useState(false);

  const last = useCallback(async () => {
    try {
      const [m, p] = await Promise.all([api.get("/admin/meetings"), api.get("/admin/task-members")]);
      setData(m.data);
      setMembers(p.data.members || p.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Kunne ikke hente møter");
    }
  }, []);

  useEffect(() => { last(); }, [last]);

  const avlys = async (mote) => {
    if (!window.confirm(`Avlyse «${mote.subject}»? Deltakerne får avlysning i Outlook.`)) return;
    try {
      await api.post(`/admin/meetings/${mote.id}/cancel`);
      toast.success("Møtet er avlyst");
      last();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Kunne ikke avlyse");
    }
  };

  const naa = lokalNaa();
  const alle = data?.meetings || [];
  const kommende = alle.filter((m) => !m.cancelled && (m.end || m.start) >= naa);
  const tidligere = alle.filter((m) => m.cancelled || (m.end || m.start) < naa).reverse();

  return (
    <div className="bud-x" data-testid="moter-admin">
      <header className="flex flex-wrap items-start justify-between gap-3 mb-7">
        <div>
          <p className="adm-eyebrow">Internt</p>
          <h1 className="adm-title mt-1">Møter</h1>
          <p className="adm-sub mt-1.5">Outlook-invitasjoner og Teams-møter sendes automatisk til deltakerne{data?.organizer ? ` · arrangør ${data.organizer}` : ""}</p>
        </div>
        <button type="button" onClick={() => setNyttMote(true)} className="adm-btn adm-btn-sm" data-testid="moter-nytt-btn">
          <Plus size={13} /> Nytt møte
        </button>
      </header>

      {data && !data.configured && (
        <p className="mb-5 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="moter-ikke-konfigurert">
          Microsoft-integrasjonen er ikke konfigurert — møter kan ikke opprettes.
        </p>
      )}

      {!data ? (
        <div className="space-y-2 pt-2" aria-hidden>
          {[0, 1, 2].map((i) => <div key={i} className="sak-skjelett h-[52px] rounded-[10px]" />)}
        </div>
      ) : alle.length === 0 ? (
        <div className="text-center py-20" data-testid="moter-tom">
          <CalendarX2 size={22} strokeWidth={1.4} className="mx-auto text-neutral-300" />
          <p className="mt-3 text-sm font-medium text-neutral-700">Ingen møter ennå</p>
          <p className="text-xs text-neutral-400 mt-1.5">Opprett det første møtet — invitasjonene sendes fra Outlook.</p>
          <button type="button" onClick={() => setNyttMote(true)} className="mt-4 adm-btn adm-btn-sm" data-testid="moter-tom-nytt">
            <Plus size={13} /> Nytt møte
          </button>
        </div>
      ) : (
        <>
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-neutral-500 pb-2 border-b-2 border-neutral-900/80">
            Kommende <span className="bud-mono text-neutral-400 ml-1">{kommende.length}</span>
          </p>
          <div data-testid="moter-kommende">
            {kommende.map((m) => <MoteRad key={m.id} mote={m} onAvlys={avlys} passert={false} />)}
            {kommende.length === 0 && <p className="text-[12.5px] text-neutral-400 py-6">Ingen kommende møter.</p>}
          </div>
          {tidligere.length > 0 && (
            <>
              <p className="mt-9 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-neutral-400 pb-2 border-b border-[#E7E4DB]">
                Tidligere og avlyste <span className="bud-mono ml-1">{tidligere.length}</span>
              </p>
              <div data-testid="moter-tidligere">
                {tidligere.map((m) => <MoteRad key={m.id} mote={m} onAvlys={avlys} passert />)}
              </div>
            </>
          )}
        </>
      )}

      {nyttMote && (
        <NyttMoteModal
          members={members}
          organizer={data?.organizer || ""}
          onLagret={() => { setNyttMote(false); last(); }}
          onLukk={() => setNyttMote(false)}
        />
      )}
    </div>
  );
}
